import React, { useEffect, useMemo, useState } from 'react';
import { Edit2, Users, Trash2 } from 'lucide-react';

// Formater une date ISO en français SANS conversion de timezone
// On extrait directement la date/heure du string ISO
const frDT = (iso) => {
  if (!iso) return '';
  try {
    // Extraire YYYY-MM-DD et HH:MM directement
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hours, minutes] = match;
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    // Fallback avec getUTC pour éviter conversion timezone
    const d = new Date(iso);
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch { return ''; }
};

const sortFn = (a, b, key, dir) => {
  let va = a[key]; let vb = b[key];
  if (key === 'start_datetime' || key === 'end_datetime') {
    va = new Date(va).getTime(); vb = new Date(vb).getTime();
  }
  const res = ('' + va).localeCompare('' + vb, 'fr', { sensitivity: 'base', numeric: true });
  return dir === 'asc' ? res : -res;
};

export default function EventsDataTable({ onEdit, onViewRegs, onDelete, refreshKey = 0 }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const pageSizeOptions = [25, 50, 100, 150, 200, 250];
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: 'start_datetime', dir: 'desc' });

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const url = API_BASE ? new URL('/api/admin/events', API_BASE).toString() : '/api/admin/events';
      const headers = { 'Content-Type': 'application/json' };
      const token = (typeof window !== 'undefined') ? localStorage.getItem('authToken') : null;
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      const items = (data?.events || []).map((e, idx) => ({
        id: e.id,
        title: e.title,
        start_datetime: e.start_datetime,
        end_datetime: e.end_datetime,
        is_public: !!e.is_public,
        global_quota: e.global_quota,
        tariffs_count: Array.isArray(e.tariffs) ? e.tariffs.length : 0,
        registrations_paid: e.registrations_paid || 0,
        registrations_total: e.registrations_total || 0,
      }));
      setRows(items);
      setPage(1);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [refreshKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = rows.filter(r => (
        (r.title || '').toLowerCase().includes(q) ||
        frDT(r.start_datetime).toLowerCase().includes(q) ||
        frDT(r.end_datetime).toLowerCase().includes(q)
      ));
    }
    const s = [...list].sort((a,b) => sortFn(a,b, sort.key, sort.dir));
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return { total: s.length, pageRows: s.slice(start, end).map((r, i) => ({...r, __rownum: start + i + 1})) };
  }, [rows, search, page, pageSize, sort]);

  const toggleSort = (key) => setSort(p => p.key !== key ? { key, dir: 'asc' } : { key, dir: p.dir === 'asc' ? 'desc' : 'asc' });

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="flex items-center justify-between p-4">
        <div className="text-sm flex items-center space-x-2" style={{ fontFamily: '"Lora", serif' }}>
          <span>Show</span>
          <select value={pageSize} onChange={(e)=>{ setPageSize(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1">
            {pageSizeOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <span>entries</span>
        </div>
        <div className="text-sm" style={{ fontFamily: '"Lora", serif' }}>
          <label className="mr-2">Search:</label>
          <input value={search} onChange={(e)=>{ setSearch(e.target.value); setPage(1); }} className="border rounded px-2 py-1" placeholder="Rechercher..." />
        </div>
      </div>

      <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
        <table className="min-w-full border-t" style={{ tableLayout: 'auto', whiteSpace: 'nowrap' }}>
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: '__rownum', label: 'N°', nosort: true },
                { key: 'title', label: 'Titre' },
                { key: 'start_datetime', label: 'Début' },
                { key: 'end_datetime', label: 'Fin' },
                { key: 'is_public', label: 'Public' },
                { key: 'global_quota', label: 'Quota' },
                { key: 'tariffs_count', label: 'Tarifs' },
                { key: 'registrations', label: 'Inscrits', nosort: true },
                { key: 'actions', label: 'Actions', nosort: true },
              ].map(col => (
                <th key={col.key} className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">
                  <div className={`flex items-center ${col.nosort ? '' : 'cursor-pointer'}`} onClick={() => !col.nosort && toggleSort(col.key)}>
                    <span>{col.label}</span>
                    {!col.nosort && (
                      <span className="ml-1 text-xs text-gray-400">{sort.key === col.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={9}>Chargement...</td></tr>}
            {!loading && error && <tr><td className="px-3 py-4 text-center text-red-600" colSpan={9}>{error}</td></tr>}
            {!loading && !error && filtered.total === 0 && <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={9}>Aucun résultat</td></tr>}
            {!loading && !error && filtered.pageRows.map(r => (
              <tr key={r.id} className="odd:bg-white even:bg-gray-50 hover:bg-blue-50">
                <td className="px-3 py-2 text-xs text-gray-700 border-b">{r.__rownum}</td>
                <td className="px-3 py-2 text-sm text-gray-900 border-b font-medium">{r.title}</td>
                <td className="px-3 py-2 text-xs text-gray-600 border-b">{frDT(r.start_datetime)}</td>
                <td className="px-3 py-2 text-xs text-gray-600 border-b">{frDT(r.end_datetime)}</td>
                <td className="px-3 py-2 text-xs text-gray-600 border-b">{r.is_public ? 'Oui' : 'Non'}</td>
                <td className="px-3 py-2 text-xs text-gray-900 border-b text-center font-semibold">{r.global_quota ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-900 border-b text-center font-semibold">{r.tariffs_count}</td>
                <td className="px-3 py-2 text-xs border-b text-center">
                  <span className="px-2 py-1 rounded-full bg-blue-100 text-gray-900 font-semibold">
                    {r.registrations_paid}/{r.registrations_total}
                  </span>
                </td>
                <td className="px-3 py-2 border-b">
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => onEdit && onEdit(r.id)} 
                      className="px-2 py-1 rounded text-white text-xs font-medium flex items-center gap-1 transition-all hover:opacity-80"
                      style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                      title="Modifier"
                    >
                      <Edit2 className="w-3 h-3" />
                      <span>Modifier</span>
                    </button>
                    <button 
                      onClick={() => onViewRegs && onViewRegs(r.id)} 
                      className="px-2 py-1 rounded bg-blue-600 text-white text-xs font-medium flex items-center gap-1 transition-all hover:bg-blue-700"
                      title="Voir inscriptions"
                    >
                      <Users className="w-3 h-3" />
                      <span>Inscrits</span>
                    </button>
                    <button 
                      onClick={() => onDelete && onDelete(r.id)} 
                      className="px-2 py-1 rounded bg-red-600 text-white text-xs font-medium flex items-center gap-1 transition-all hover:bg-red-700"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between p-4 text-sm" style={{ fontFamily: '"Lora", serif' }}>
        <div>
          {filtered.total > 0 && (
            <span>Affichage {Math.min((page-1)*pageSize+1, filtered.total)} à {Math.min(page*pageSize, filtered.total)} sur {filtered.total} entrées</span>
          )}
        </div>
        <div className="space-x-2">
          <button onClick={() => setPage(p => Math.max(1, p-1))} className="px-3 py-1 border rounded disabled:opacity-50" disabled={page === 1}>Précédent</button>
          <button onClick={() => setPage(p => ((p*pageSize) < filtered.total ? p+1 : p))} className="px-3 py-1 border rounded disabled:opacity-50" disabled={(page*pageSize) >= filtered.total}>Suivant</button>
        </div>
      </div>
    </div>
  );
}